import { useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import { PageLayout } from '@/components/landing/PageLayout'
import { PageHeader, SectionDivider } from '@/components/landing/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import {
  Copy,
  Check,
  Key,
  Video,
  MessageSquare,
  Webhook,
  Gauge,
  Compass,
  Shield,
  RotateCcw,
} from 'lucide-react'

function CodeBlock({ code, language = 'bash' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false)

  function copyToClipboard() {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-7 w-7 text-muted-foreground hover:text-foreground"
        onClick={copyToClipboard}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
      <pre className="overflow-x-auto rounded-lg bg-zinc-950 p-4 text-sm leading-relaxed">
        <code className="font-mono text-zinc-300">{code}</code>
      </pre>
    </div>
  )
}

function EndpointCard({
  method,
  path,
  description,
  headers,
  body,
  response,
}: {
  method: string
  path: string
  description: string
  headers?: string[]
  body?: string
  response?: string
}) {
  const methodColors: Record<string, string> = {
    GET: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
    POST: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    DELETE: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
    PUT: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    PATCH: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  }

  return (
    <div className="surface-card p-5">
      <div className="flex items-start gap-3">
        <Badge variant="outline" className={methodColors[method] || ''}>
          {method}
        </Badge>
        <div className="min-w-0 flex-1 space-y-1">
          <code className="break-all font-mono text-sm font-medium">{path}</code>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      {(headers || body || response) && (
        <div className="mt-4 space-y-3">
          {headers && (
            <div>
              <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Required Headers
              </p>
              <div className="flex flex-wrap gap-1.5">
                {headers.map((h) => (
                  <Badge key={h} variant="secondary" className="font-mono text-xs">
                    {h}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {body && (
            <div>
              <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Request Body
              </p>
              <CodeBlock code={body} language="json" />
            </div>
          )}
          {response && (
            <div>
              <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Response
              </p>
              <CodeBlock code={response} language="json" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function Docs() {
  return (
    <>
      <Head>
        <title>API Documentation — KonexZero</title>
        <meta name="description" content="Complete REST API reference for KonexZero. Authentication, room management, WebRTC signaling, webhooks, and code examples." />
      </Head>
      <PageLayout maxWidth="7xl">
        <div className="space-y-10">
          <PageHeader
            eyebrow="Documentation"
            title="API Documentation"
            description="Everything you need to integrate realtime voice, video, and screen sharing into your application using simple REST calls."
          />

        {/* API Explorer callout — surface-card with brand bloom, no tinted border */}
        <div className="surface-card relative overflow-hidden p-6">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full"
            style={{
              background:
                'radial-gradient(circle, hsl(var(--brand-2) / 0.22) 0%, transparent 70%)',
            }}
          />
          <div className="relative flex items-start gap-4">
            <Compass className="mt-0.5 h-8 w-8 shrink-0 text-primary" />
            <div className="space-y-2">
              <h3 className="font-heading text-lg font-semibold">Interactive API Explorer</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Try the API directly in your browser with our interactive explorer powered by Scalar.
                Test endpoints, view request/response schemas, and generate code snippets in any language.
              </p>
              <div className="flex flex-wrap gap-3 pt-1">
                <Button size="sm" asChild>
                  <Link href="/explorer">Open API Explorer</Link>
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a href="/api/v1/docs/openapi" target="_blank" rel="noopener noreferrer">
                    Download OpenAPI Spec
                  </a>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Import the OpenAPI spec into Postman, Insomnia, or any HTTP client that supports OpenAPI 3.1.
              </p>
            </div>
          </div>
        </div>

        <SectionDivider />

        {/* Quick Start */}
        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold">Quick Start</h2>
          <div className="space-y-3">
            <p className="text-muted-foreground leading-relaxed">
              Get up and running in three steps:
            </p>
            <ol className="list-decimal pl-6 space-y-3 text-muted-foreground leading-relaxed">
              <li>
                <strong className="text-foreground">Create an application</strong> in your{' '}
                <Link href="/dashboard/applications/new" className="text-primary hover:underline">
                  dashboard
                </Link>{' '}
                to get your App ID and API Key.
              </li>
              <li>
                <strong className="text-foreground">Join a room</strong> by making a POST request
                with your credentials. The room is created automatically if it doesn't exist.
              </li>
              <li>
                <strong className="text-foreground">Publish and subscribe</strong> to media tracks
                using standard WebRTC SDP negotiation via our REST API.
              </li>
            </ol>
          </div>
        </section>

        <SectionDivider />

        {/* Authentication */}
        <section className="space-y-4" id="authentication">
          <div className="flex items-center gap-2">
            <Key className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-2xl font-semibold">Authentication</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            All API requests require two headers for authentication:
          </p>
          <CodeBlock
            code={`X-App-Id: your_app_id
X-Api-Key: your_api_secret`}
          />
          <p className="text-muted-foreground leading-relaxed">
            You can find your App ID in the dashboard. The API Key is shown once when the
            application is created. If you lose it, you can rotate to generate a new one.
          </p>

          <div className="surface-card flex items-start gap-3 p-4">
            <span className="mt-1 inline-block h-3 w-1 shrink-0 rounded-full bg-amber-500/70" aria-hidden />
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Security note:</strong> Never expose your API
              Key in client-side code. All API calls should be made from your backend server.
              The API Key is a secret and should be treated like a password.
            </p>
          </div>
        </section>

        <SectionDivider />

        {/* Base URL */}
        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold">Base URL</h2>
          <CodeBlock code="https://api.konexzero.com/api/v1" />
          <p className="text-muted-foreground text-sm">
            All endpoints below are relative to this base URL.
          </p>
        </section>

        <SectionDivider />

        {/* API Reference */}
        <section className="space-y-6">
          <h2 className="font-heading text-2xl font-semibold">API Reference</h2>

          <Tabs defaultValue="rooms">
            <TabsList className="mb-4">
              <TabsTrigger value="rooms" className="gap-1.5">
                <Video className="h-3.5 w-3.5" />
                Rooms
              </TabsTrigger>
              <TabsTrigger value="credentials" className="gap-1.5">
                <Key className="h-3.5 w-3.5" />
                Credentials
              </TabsTrigger>
              <TabsTrigger value="messages" className="gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" />
                Messages
              </TabsTrigger>
            </TabsList>

            {/* Rooms Tab */}
            <TabsContent value="rooms" className="space-y-4">
              <EndpointCard
                method="POST"
                path="/rooms/:room_id/join"
                description="Join a room. Creates the room automatically if it doesn't exist. Fast path: pre-fetch ICE servers via POST /credentials, build your RTCPeerConnection and SDP offer before joining, and include tracks + session_description here — the response then carries a publish object with the SDP answer, collapsing join+publish into one round trip. Pass an optional resume_session_id to recover a participant after a transient WebSocket drop instead of allocating a fresh SFU session — the response data.resumed flag tells you which path was taken."
                headers={['X-App-Id', 'X-Api-Key', 'Content-Type: application/json']}
                body={`{
  "participant_id": "user-123",
  "display_name": "Alice",
  "resume_session_id": "<optional — prior session_id>",
  "tracks": [                       // optional — publish in the same request
    { "track_name": "audio", "kind": "audio", "mid": "0" }
  ],
  "session_description": {          // optional — your SDP offer
    "type": "offer",
    "sdp": "v=0\\r\\n..."
  }
}`}
                response={`{
  "data": {
    "session_id": "cf-session-abc123",
    "resumed": false,
    "ice_servers": [
      {
        "urls": "turn:turn.example.com:3478",
        "username": "...",
        "credential": "..."
      }
    ],
    "ws_token": "...",
    "publish": {                    // present when tracks were sent
      "session_description": { "type": "answer", "sdp": "v=0\\r\\n..." },
      "tracks": [ { "track_name": "audio", "mid": "0", "kind": "audio" } ],
      "requires_immediate_renegotiation": false
    }
  },
  "meta": { "timestamp": "2026-03-12T10:30:45.123Z" }
}`}
              />

              <EndpointCard
                method="POST"
                path="/rooms/:room_id/publish"
                description="Publish local media tracks (audio, video, screen share) to the room. Each track is identified by name + kind + the mid from your local SDP offer; the response carries the SDP answer. Note: other participants are NOT notified yet — apply the answer, wait until your outbound-rtp stats report bytesSent > 0, then call /publish/confirm to announce the tracks. This guarantees subscribers never pull a track whose media isn't flowing."
                headers={['X-App-Id', 'X-Api-Key', 'X-Participant-Id', 'Content-Type: application/json']}
                body={`{
  "tracks": [
    { "track_name": "video", "kind": "video", "mid": "0" },
    { "track_name": "audio", "kind": "audio", "mid": "1" }
  ],
  "session_description": {
    "type": "offer",
    "sdp": "v=0\\r\\n..."
  }
}`}
                response={`{
  "data": {
    "session_description": { "type": "answer", "sdp": "v=0\\r\\n..." },
    "tracks": [
      { "track_name": "video", "kind": "video", "mid": "0" },
      { "track_name": "audio", "kind": "audio", "mid": "1" }
    ],
    "requires_immediate_renegotiation": false
  },
  "meta": { "timestamp": "2026-03-12T10:30:45.123Z" }
}`}
              />

              <EndpointCard
                method="POST"
                path="/rooms/:room_id/publish/confirm"
                description="Announce previously published tracks once media is actually flowing (your outbound-rtp stats show bytesSent > 0). Marks the tracks live and fires the track_published room event — subscribers reacting to that event can pull immediately without hitting tracks_not_ready."
                headers={['X-App-Id', 'X-Api-Key', 'X-Participant-Id', 'Content-Type: application/json']}
                body={`{
  "track_names": ["video", "audio"]
}`}
                response={`{
  "data": { "live_tracks": ["video", "audio"] },
  "meta": { "timestamp": "2026-03-12T10:30:45.123Z" }
}`}
              />

              <EndpointCard
                method="POST"
                path="/rooms/:room_id/unpublish"
                description="Stop publishing specific tracks without leaving the room."
                headers={['X-App-Id', 'X-Api-Key', 'X-Participant-Id', 'Content-Type: application/json']}
                body={`{
  "track_names": ["video"],
  "force": false
}`}
              />

              <EndpointCard
                method="POST"
                path="/rooms/:room_id/subscribe"
                description="Subscribe to another participant's published tracks. Reference them by participant_id and track names — never by SFU session_id. Fast path (recommended): pre-add one recvonly transceiver per subscribed track, include your SDP offer as session_description, and the response carries an SDP answer — apply it and you're done, no /subscribe/answer call. Legacy path: omit session_description and the response is an SDP offer you must complete via /subscribe/answer. On 503 tracks_not_ready, retry after the milliseconds in the Retry-After-Ms response header."
                headers={['X-App-Id', 'X-Api-Key', 'X-Participant-Id', 'Content-Type: application/json']}
                body={`{
  "subscriptions": [
    {
      "participant_id": "user-123",
      "track_names": ["video", "audio"]
    }
  ],
  "session_description": {          // optional — your recvonly SDP offer
    "type": "offer",
    "sdp": "v=0\\r\\n..."
  }
}`}
                response={`{
  "data": {
    "session_description": { "type": "answer", "sdp": "v=0\\r\\n..." },
    "tracks": [
      { "track_name": "video", "mid": "0" },
      { "track_name": "audio", "mid": "1" }
    ]
  },
  "meta": { "timestamp": "2026-03-12T10:30:45.123Z" }
}`}
              />

              <EndpointCard
                method="POST"
                path="/rooms/:room_id/subscribe/answer"
                description="Legacy path only: complete the subscribe handshake by sending your SDP answer. Not needed when you included your own offer in /subscribe (the fast path)."
                headers={['X-App-Id', 'X-Api-Key', 'X-Participant-Id', 'Content-Type: application/json']}
                body={`{
  "session_description": {
    "type": "answer",
    "sdp": "v=0\\r\\n..."
  }
}`}
              />

              <EndpointCard
                method="POST"
                path="/rooms/:room_id/unsubscribe"
                description="Unsubscribe from a participant's tracks."
                headers={['X-App-Id', 'X-Api-Key', 'X-Participant-Id', 'Content-Type: application/json']}
                body={`{
  "track_names": ["video"],
  "session_id": "publisher-session-id"
}`}
              />

              <EndpointCard
                method="POST"
                path="/rooms/:room_id/renegotiate"
                description="Renegotiate the WebRTC session (e.g., after adding/removing tracks)."
                headers={['X-App-Id', 'X-Api-Key', 'X-Participant-Id', 'Content-Type: application/json']}
                body={`{
  "session_description": {
    "type": "offer",
    "sdp": "v=0\\r\\n..."
  }
}`}
              />

              <EndpointCard
                method="POST"
                path="/rooms/:room_id/leave"
                description="Leave the room and clean up all associated tracks and subscriptions."
                headers={['X-App-Id', 'X-Api-Key', 'X-Participant-Id']}
              />

              <EndpointCard
                method="GET"
                path="/rooms/:room_id"
                description="Get room details including list of current participants and their tracks."
                headers={['X-App-Id', 'X-Api-Key']}
                response={`{
  "room": {
    "id": "app_id_room_name",
    "name": "my-room",
    "status": "active",
    "participants": [
      {
        "participant_id": "user-123",
        "display_name": "Alice",
        "tracks": ["video", "audio"]
      }
    ]
  }
}`}
              />

              <EndpointCard
                method="GET"
                path="/rooms/:room_id/participants"
                description="List all current participants in a room."
                headers={['X-App-Id', 'X-Api-Key']}
              />
            </TabsContent>

            {/* Credentials Tab */}
            <TabsContent value="credentials" className="space-y-4">
              <EndpointCard
                method="POST"
                path="/credentials"
                description="Generate temporary TURN server credentials for client-side WebRTC peer connections."
                headers={['X-App-Id', 'X-Api-Key', 'Content-Type: application/json']}
                body={`{
  "ttl": 86400
}`}
                response={`{
  "ice_servers": [
    {
      "urls": "turn:turn.example.com:3478",
      "username": "timestamp:random",
      "credential": "..."
    }
  ],
  "ttl": 86400
}`}
              />

              <div className="surface-card flex items-start gap-3 p-4">
                <span className="mt-1 inline-block h-3 w-1 shrink-0 rounded-full bg-blue-500/70" aria-hidden />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Tip:</strong> TURN credentials should be
                  generated on your server and passed to the client. They expire after the
                  specified TTL (in seconds). The default TTL is 86400 (24 hours).
                </p>
              </div>
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" className="space-y-4">
              <EndpointCard
                method="POST"
                path="/rooms/:room_id/messages"
                description="Send a data message to all participants in the room via the data channel."
                headers={['X-App-Id', 'X-Api-Key', 'X-Participant-Id', 'Content-Type: application/json']}
                body={`{
  "type": "chat",
  "payload": {
    "text": "Hello everyone!"
  }
}`}
              />

              <EndpointCard
                method="GET"
                path="/rooms/:room_id/messages"
                description="Retrieve recent messages sent in a room."
                headers={['X-App-Id', 'X-Api-Key']}
                response={`{
  "messages": [
    {
      "id": "msg-uuid",
      "type": "chat",
      "participant_id": "user-123",
      "payload": { "text": "Hello everyone!" },
      "created_at": "2026-03-12T10:00:00Z"
    }
  ]
}`}
              />
            </TabsContent>
          </Tabs>
        </section>

        <SectionDivider />

        {/* Realtime signals & presence */}
        <section className="space-y-4" id="realtime">
          <h2 className="font-heading text-2xl font-semibold">Realtime Signals &amp; Presence</h2>
          <p className="text-muted-foreground leading-relaxed">
            Every <code className="text-foreground">/join</code> response includes a{' '}
            <code className="text-foreground">ws_token</code>. Connect it to the signaling
            WebSocket to receive room events and to maintain presence:
          </p>
          <CodeBlock
            code={`const ws = new WebSocket(\`wss://api.konexzero.com/cable?token=\${wsToken}\`)
const identifier = JSON.stringify({ channel: 'RoomChannel', room_id: roomId })

ws.onopen = () => ws.send(JSON.stringify({ command: 'subscribe', identifier }))
ws.onmessage = (e) => {
  const msg = JSON.parse(e.data)
  if (msg.message?.type === 'track_published') {
    // subscribe to msg.message.tracks — media is guaranteed to be flowing
  }
}

// Presence heartbeat — send every ~20 seconds
setInterval(() => {
  ws.send(JSON.stringify({ command: 'message', identifier, data: JSON.stringify({ action: 'heartbeat' }) }))
}, 20_000)`}
          />
          <p className="text-muted-foreground leading-relaxed text-sm">
            Events delivered on the stream:{' '}
            <code className="text-foreground">participant_joined</code>,{' '}
            <code className="text-foreground">track_published</code> (fires at{' '}
            <code className="text-foreground">/publish/confirm</code>, i.e. only when the
            publisher&apos;s media is verifiably flowing),{' '}
            <code className="text-foreground">track_unpublished</code>,{' '}
            <code className="text-foreground">media_state_changed</code> and{' '}
            <code className="text-foreground">participant_left</code>.
          </p>
          <div className="surface-card flex items-start gap-3 p-4">
            <span className="mt-1 inline-block h-3 w-1 shrink-0 rounded-full bg-amber-500/70" aria-hidden />
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Presence &amp; eviction:</strong> joining grants
              a 90-second grace window, and every authenticated REST signaling call refreshes your
              liveness. After that, you must keep presence alive — either via WebSocket heartbeats
              (recommended, ~20s interval) or ongoing REST activity. Participants with no
              liveness signal for ~30–90 seconds are evicted from the room and a{' '}
              <code className="text-foreground">participant_left</code> event with{' '}
              <code className="text-foreground">evicted: true</code> is broadcast.
            </p>
          </div>
        </section>

        <SectionDivider />

        {/* Status endpoint */}
        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold">Status &amp; Health</h2>
          <EndpointCard
            method="GET"
            path="/status"
            description="Check API status. Returns the current state of the service."
            headers={['X-App-Id', 'X-Api-Key']}
            response={`{
  "status": "ok",
  "timestamp": "2026-03-12T10:00:00Z"
}`}
          />
          <p className="text-sm text-muted-foreground">
            For unauthenticated health checks (e.g., load balancer probes), use{' '}
            <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">/healthz</code>.
          </p>
        </section>

        <SectionDivider />

        {/* Webhooks */}
        <section className="space-y-6" id="webhooks">
          <div className="flex items-center gap-2">
            <Webhook className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-2xl font-semibold">Webhooks</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Receive real-time notifications about room and participant events via HTTP callbacks.
            Configure webhook endpoints in the{' '}
            <Link href="/dashboard/webhooks" className="text-primary hover:underline">
              dashboard
            </Link>{' '}
            or via the API.
          </p>

          {/* Event Types */}
          <div className="space-y-3">
            <h3 className="font-heading text-lg font-semibold">Event Types</h3>
            <div className="surface-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Event</th>
                    <th className="text-left p-3 font-medium">Trigger</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3 font-mono text-xs">room.started</td>
                    <td className="p-3 text-muted-foreground">First participant joins a room</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-mono text-xs">room.ended</td>
                    <td className="p-3 text-muted-foreground">Last participant leaves a room</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-mono text-xs">participant.joined</td>
                    <td className="p-3 text-muted-foreground">A participant joins a room</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-mono text-xs">participant.left</td>
                    <td className="p-3 text-muted-foreground">A participant leaves (voluntary, disconnect, or eviction)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Payload Examples */}
          <div className="space-y-3">
            <h3 className="font-heading text-lg font-semibold">Payload Examples</h3>
            <Tabs defaultValue="room_started">
              <TabsList className="mb-3">
                <TabsTrigger value="room_started">room.started</TabsTrigger>
                <TabsTrigger value="participant_joined">participant.joined</TabsTrigger>
                <TabsTrigger value="participant_left">participant.left</TabsTrigger>
              </TabsList>

              <TabsContent value="room_started">
                <CodeBlock
                  code={`{
  "event": "room.started",
  "timestamp": "2026-03-12T10:00:00Z",
  "data": {
    "room_name": "my-room",
    "app_id": "app_abc123"
  }
}`}
                  language="json"
                />
              </TabsContent>

              <TabsContent value="participant_joined">
                <CodeBlock
                  code={`{
  "event": "participant.joined",
  "timestamp": "2026-03-12T10:00:05Z",
  "data": {
    "room_name": "my-room",
    "participant_id": "user-123",
    "display_name": "Alice",
    "app_id": "app_abc123"
  }
}`}
                  language="json"
                />
              </TabsContent>

              <TabsContent value="participant_left">
                <CodeBlock
                  code={`{
  "event": "participant.left",
  "timestamp": "2026-03-12T10:05:00Z",
  "data": {
    "room_name": "my-room",
    "participant_id": "user-123",
    "reason": "voluntary",
    "app_id": "app_abc123"
  }
}`}
                  language="json"
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Webhook Headers */}
          <div className="space-y-3">
            <h3 className="font-heading text-lg font-semibold">Delivery Headers</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Each webhook delivery includes the following headers for identification and verification:
            </p>
            <div className="surface-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Header</th>
                    <th className="text-left p-3 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3 font-mono text-xs">X-KonexZero-Signature</td>
                    <td className="p-3 text-muted-foreground">HMAC-SHA256 signature for verification</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-mono text-xs">X-KonexZero-Event</td>
                    <td className="p-3 text-muted-foreground">Event type (e.g., room.started)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-mono text-xs">X-KonexZero-Delivery</td>
                    <td className="p-3 text-muted-foreground">Unique delivery ID (UUID)</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-mono text-xs">X-KonexZero-Timestamp</td>
                    <td className="p-3 text-muted-foreground">Unix timestamp of the delivery</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Signature Verification */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Signature Verification</h3>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Verify the <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">X-KonexZero-Signature</code> header
              to ensure webhooks are genuinely from KonexZero. The signature is computed as{' '}
              <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">HMAC-SHA256(timestamp.payload, secret)</code>.
            </p>

            <Tabs defaultValue="node">
              <TabsList className="mb-3">
                <TabsTrigger value="node">Node.js</TabsTrigger>
                <TabsTrigger value="python">Python</TabsTrigger>
              </TabsList>

              <TabsContent value="node">
                <CodeBlock
                  code={`const crypto = require('crypto');

function verifyWebhook(payload, secret, timestamp, signature) {
  const signedPayload = \`\${timestamp}.\${payload}\`;
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(signedPayload)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(signature)
  );
}

// In your Express handler:
app.post('/webhooks/konexzero', (req, res) => {
  const payload = JSON.stringify(req.body);
  const timestamp = req.headers['x-konexzero-timestamp'];
  const signature = req.headers['x-konexzero-signature'];

  if (!verifyWebhook(payload, WEBHOOK_SECRET, timestamp, signature)) {
    return res.status(401).send('Invalid signature');
  }

  // Process the event...
  res.status(200).send('OK');
});`}
                  language="javascript"
                />
              </TabsContent>

              <TabsContent value="python">
                <CodeBlock
                  code={`import hmac
import hashlib

def verify_webhook(payload: str, secret: str, timestamp: str, signature: str) -> bool:
    signed_payload = f"{timestamp}.{payload}"
    expected = "sha256=" + hmac.new(
        secret.encode(), signed_payload.encode(), hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, signature)

# In your Flask handler:
@app.route('/webhooks/konexzero', methods=['POST'])
def handle_webhook():
    payload = request.get_data(as_text=True)
    timestamp = request.headers.get('X-KonexZero-Timestamp')
    signature = request.headers.get('X-KonexZero-Signature')

    if not verify_webhook(payload, WEBHOOK_SECRET, timestamp, signature):
        return 'Invalid signature', 401

    # Process the event...
    return 'OK', 200`}
                  language="python"
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Retry Policy */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Retry Policy</h3>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Failed deliveries (non-2xx response or timeout) are retried up to 5 times with
              exponential backoff:
            </p>
            <div className="surface-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Attempt</th>
                    <th className="text-left p-3 font-medium">Delay After Failure</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3">1st retry</td>
                    <td className="p-3 text-muted-foreground">30 seconds</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3">2nd retry</td>
                    <td className="p-3 text-muted-foreground">2 minutes</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3">3rd retry</td>
                    <td className="p-3 text-muted-foreground">15 minutes</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3">4th retry</td>
                    <td className="p-3 text-muted-foreground">1 hour</td>
                  </tr>
                  <tr>
                    <td className="p-3">5th retry</td>
                    <td className="p-3 text-muted-foreground">4 hours</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="surface-card flex items-start gap-3 p-4">
              <span className="mt-1 inline-block h-3 w-1 shrink-0 rounded-full bg-amber-500/70" aria-hidden />
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Tip:</strong> Your endpoint should return a
                2xx status code within 30 seconds to be considered successful. Process webhook
                payloads asynchronously if your handling takes longer.
              </p>
            </div>
          </div>

          {/* Webhook Management API */}
          <div className="space-y-3">
            <h3 className="font-heading text-lg font-semibold">Webhook Management API</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Manage webhook endpoints programmatically:
            </p>
            <div className="space-y-3">
              <EndpointCard
                method="GET"
                path="/webhooks"
                description="List all webhook endpoints for the current application."
                headers={['X-App-Id', 'X-Api-Key']}
              />
              <EndpointCard
                method="POST"
                path="/webhooks"
                description="Create a new webhook endpoint. Returns the signing secret (shown once)."
                headers={['X-App-Id', 'X-Api-Key', 'Content-Type: application/json']}
                body={`{
  "url": "https://example.com/webhooks/konexzero",
  "description": "Production webhook",
  "subscribed_events": ["room.started", "room.ended", "participant.joined", "participant.left"]
}`}
              />
              <EndpointCard
                method="GET"
                path="/webhooks/:id"
                description="Fetch one endpoint plus its 20 most recent delivery attempts."
                headers={['X-App-Id', 'X-Api-Key']}
              />
              <EndpointCard
                method="PATCH"
                path="/webhooks/:id"
                description="Update url, description, active flag, or subscribed events."
                headers={['X-App-Id', 'X-Api-Key', 'Content-Type: application/json']}
                body={`{
  "active": false,
  "subscribed_events": ["room.ended"]
}`}
              />
              <EndpointCard
                method="DELETE"
                path="/webhooks/:id"
                description="Permanently delete a webhook endpoint and its delivery history."
                headers={['X-App-Id', 'X-Api-Key']}
              />
              <EndpointCard
                method="POST"
                path="/webhooks/:id/rotate_secret"
                description="Generate a new signing secret for an endpoint. The old secret is invalidated immediately."
                headers={['X-App-Id', 'X-Api-Key']}
              />
              <EndpointCard
                method="POST"
                path="/webhooks/:id/test"
                description="Send a test event to verify your endpoint is reachable."
                headers={['X-App-Id', 'X-Api-Key']}
              />
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* Rate Limits */}
        <section className="space-y-6" id="rate-limits">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-2xl font-semibold">Rate Limits</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            API requests are rate-limited per credential to ensure fair usage and platform stability.
            Limits are applied independently per API key.
          </p>

          {/* Rate Limit Tiers */}
          <div className="space-y-3">
            <h3 className="font-heading text-lg font-semibold">Limit Tiers</h3>
            <div className="surface-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Scope</th>
                    <th className="text-left p-3 font-medium">Limit</th>
                    <th className="text-left p-3 font-medium">Applies To</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3 font-medium">Per-Credential</td>
                    <td className="p-3 font-mono text-xs">120 req/min</td>
                    <td className="p-3 text-muted-foreground">All authenticated API endpoints</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-medium">Signaling</td>
                    <td className="p-3 font-mono text-xs">60 req/min</td>
                    <td className="p-3 text-muted-foreground">join, publish, unpublish, subscribe, unsubscribe, renegotiate, leave</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-medium">Unauthenticated</td>
                    <td className="p-3 font-mono text-xs">30 req/min</td>
                    <td className="p-3 text-muted-foreground">Requests without API credentials (per IP)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Response Headers */}
          <div className="space-y-3">
            <h3 className="font-heading text-lg font-semibold">Response Headers</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Every API response includes rate limit headers so you can monitor your usage proactively:
            </p>
            <div className="surface-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Header</th>
                    <th className="text-left p-3 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-3 font-mono text-xs">X-RateLimit-Limit</td>
                    <td className="p-3 text-muted-foreground">Maximum requests allowed in the current window</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-mono text-xs">X-RateLimit-Remaining</td>
                    <td className="p-3 text-muted-foreground">Requests remaining in the current window</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-3 font-mono text-xs">X-RateLimit-Reset</td>
                    <td className="p-3 text-muted-foreground">Unix timestamp when the window resets</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-mono text-xs">Retry-After</td>
                    <td className="p-3 text-muted-foreground">Seconds to wait before retrying (only on 429)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 429 Response Example */}
          <div className="space-y-3">
            <h3 className="font-heading text-lg font-semibold">429 Too Many Requests</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              When rate limited, the API returns a 429 response in RFC 7807 format:
            </p>
            <CodeBlock
              code={`HTTP/1.1 429 Too Many Requests
Content-Type: application/json
Retry-After: 60
X-RateLimit-Limit: 120
X-RateLimit-Remaining: 0
X-RateLimit-Reset: 1741776060

{
  "type": "https://docs.konexzero.com/errors/rate-limited",
  "title": "Too Many Requests",
  "status": 429,
  "detail": "Rate limit exceeded. Maximum 120 requests per 60 seconds.",
  "suggestion": "Reduce request frequency or contact support for higher limits.",
  "docs": "https://docs.konexzero.com/errors/rate-limited"
}`}
              language="http"
            />
          </div>

          {/* Best Practices */}
          <div className="surface-card relative overflow-hidden p-5">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, hsl(var(--brand-1) / 0.18) 0%, transparent 70%)',
              }}
            />
            <h4 className="relative font-heading text-sm font-semibold">Best Practices</h4>
            <ul className="relative mt-3 space-y-2 text-sm text-muted-foreground">
              <li>
                Monitor <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">X-RateLimit-Remaining</code> headers
                to proactively throttle your requests before hitting the limit.
              </li>
              <li>
                When you receive a 429 response, wait for the duration indicated
                by <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">Retry-After</code> before retrying.
              </li>
              <li>
                Use exponential backoff for retries, not fixed intervals.
              </li>
              <li>
                Signaling actions (join, publish, subscribe, etc.) have a lower limit than
                general API calls. Batch operations where possible.
              </li>
            </ul>
          </div>
        </section>

        <SectionDivider />

        {/* Error Handling */}
        <section className="space-y-4" id="errors">
          <h2 className="font-heading text-2xl font-semibold">Error Handling</h2>
          <p className="text-muted-foreground leading-relaxed">
            All errors follow the{' '}
            <a
              href="https://www.rfc-editor.org/rfc/rfc7807"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              RFC 7807 Problem Details
            </a>{' '}
            format. Every error response includes a stable{' '}
            <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">code</code> slug
            (programmatic discriminator), a human{' '}
            <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">suggestion</code>{' '}
            field, and a <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">docs</code> link
            for resolution guidance. Branch on{' '}
            <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">code</code> in your client — the{' '}
            <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">type</code> URL is canonical but slower to compare.
          </p>
          <CodeBlock
            code={`{
  "type": "https://docs.konexzero.com/errors/not-found",
  "code": "not_found",
  "title": "Not Found",
  "status": 404,
  "detail": "Room not found or no active session.",
  "suggestion": "Check the resource identifier. Use GET /api/v1/rooms/:room_id to verify the room exists.",
  "docs": "https://docs.konexzero.com/errors/not-found"
}`}
          />
          <div className="surface-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Meaning</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3 font-mono text-xs">401</td>
                  <td className="p-3 text-muted-foreground">Invalid or missing API credentials</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-mono text-xs">403</td>
                  <td className="p-3 text-muted-foreground">Credentials valid but action not permitted</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-mono text-xs">404</td>
                  <td className="p-3 text-muted-foreground">Room, participant, or session not found</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-mono text-xs">409</td>
                  <td className="p-3 text-muted-foreground">Conflict (e.g., screen share already claimed)</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-mono text-xs">422</td>
                  <td className="p-3 text-muted-foreground">Validation error in request body</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-mono text-xs">429</td>
                  <td className="p-3 text-muted-foreground">Rate limit exceeded</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-mono text-xs">502</td>
                  <td className="p-3 text-muted-foreground">Upstream service error</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono text-xs">503</td>
                  <td className="p-3 text-muted-foreground">
                    Tracks not yet ready on the SFU edge after a recent publish (
                    <code className="font-mono text-xs">code: "tracks_not_ready"</code>
                    ). Retry after the milliseconds in the{' '}
                    <code className="font-mono text-xs">Retry-After-Ms</code>{' '}
                    response header.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <SectionDivider />

        {/* WebRTC Flow */}
        <section className="space-y-4">
          <h2 className="font-heading text-2xl font-semibold">Integration Flow</h2>
          <p className="text-muted-foreground leading-relaxed">
            A typical integration follows this sequence:
          </p>
          <div className="space-y-3">
            <div className="flex gap-3 items-start">
              <Badge variant="outline" className="mt-0.5 shrink-0">1</Badge>
              <div>
                <p className="font-medium text-sm">Your server calls POST /rooms/:room_id/join</p>
                <p className="text-sm text-muted-foreground">
                  Returns session description + ICE servers. Pass ICE servers to the client.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <Badge variant="outline" className="mt-0.5 shrink-0">2</Badge>
              <div>
                <p className="font-medium text-sm">Client creates RTCPeerConnection</p>
                <p className="text-sm text-muted-foreground">
                  Use the ICE servers from step 1. Add local tracks (getUserMedia).
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <Badge variant="outline" className="mt-0.5 shrink-0">3</Badge>
              <div>
                <p className="font-medium text-sm">Your server calls POST /rooms/:room_id/publish</p>
                <p className="text-sm text-muted-foreground">
                  Sends the local SDP offer with track info. Returns SDP answer from KonexZero.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <Badge variant="outline" className="mt-0.5 shrink-0">4</Badge>
              <div>
                <p className="font-medium text-sm">Client sets remote description</p>
                <p className="text-sm text-muted-foreground">
                  Apply the SDP answer. Media now flows through KonexZero's edge network.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <Badge variant="outline" className="mt-0.5 shrink-0">5</Badge>
              <div>
                <p className="font-medium text-sm">Subscribe to other participants</p>
                <p className="text-sm text-muted-foreground">
                  Call POST /subscribe for each remote track. Complete with POST /subscribe/answer.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <Badge variant="outline" className="mt-0.5 shrink-0">6</Badge>
              <div>
                <p className="font-medium text-sm">Leave when done</p>
                <p className="text-sm text-muted-foreground">
                  Call POST /leave to clean up. All tracks and subscriptions are automatically closed.
                </p>
              </div>
            </div>
          </div>
        </section>

        <SectionDivider />

        <section className="space-y-3">
          <h2 className="font-heading text-2xl font-semibold">Need Help?</h2>
          <p className="text-muted-foreground leading-relaxed">
            If you have questions or run into issues, we're here to help:
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li>
              Email us at{' '}
              <a href="mailto:support@konexzero.com" className="text-primary hover:underline">
                support@konexzero.com
              </a>
            </li>
            <li>
              Visit the{' '}
              <Link href="/contact" className="text-primary hover:underline">
                Contact page
              </Link>
            </li>
            <li>
              Try endpoints interactively in the{' '}
              <Link href="/explorer" className="text-primary hover:underline">
                API Explorer
              </Link>
            </li>
          </ul>
        </section>
      </div>
    </PageLayout>
    </>
  )
}
